<script setup lang="ts">
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import DolphinTextarea from '@/components/atoms/DolphinTextarea.vue'
import MessageAttachmentActions from '@/components/organisms/MessageAttachmentActions/MessageAttachmentActions.vue'
import EnqueteFormError from './EnqueteFormError.vue'
import { computed } from 'vue'
import type {
  EnqueteFormAnsweredChatMessageType,
  EnqueteFormErrorManager,
} from './formSchema'

const model = defineModel<EnqueteFormAnsweredChatMessageType | undefined>({
  required: true,
})

const props = defineProps<{
  formErrorManager: EnqueteFormErrorManager
}>()

const selections = computed({
  get: () => {
    // MessageAttachmentActions が props として undefined を受け付けず array のみを受け付けるので、変換する
    return model.value?.selections ?? []
  },
  set: newValue => {
    const newSelections = newValue.length > 0 ? newValue : undefined

    if (model.value) {
      model.value = {
        ...model.value,
        selections: newSelections,
      }
    }
  },
})

const isEnableAnsweredChatMessage = computed(() => {
  return model.value !== undefined
})

const selectionError = computed<string | undefined>(() => {
  if (!model.value || !model.value.selections) {
    return undefined
  }

  const errorSelectionIndex = model.value.selections.findIndex((_, i) => {
    return (
      props.formErrorManager.get(`answeredChatMessage.selections.${i}`) !=
      undefined
    )
  })

  return props.formErrorManager.get(
    `answeredChatMessage.selections.${errorSelectionIndex}`
  )
})

const handleChangeIsEnableAnsweredChatMessage = (enabled: boolean) => {
  if (enabled) {
    model.value = {
      body: '',
      selections: undefined,
    }
    return
  }

  const hasInput = model.value?.body || model.value?.selections
  if (hasInput) {
    if (!confirm('入力内容が破棄されますが、よろしいですか？')) {
      return
    }
  }

  model.value = undefined
}
</script>

<template>
  <section>
    <label :class="$style.isSend">
      <DolphinCheckbox
        :model-value="isEnableAnsweredChatMessage"
        @update:model-value="handleChangeIsEnableAnsweredChatMessage"
      />
      <span>回答後にチャットメッセージを送信する</span>
    </label>
    <div v-if="model" :class="$style.message">
      <div :class="$style.formRow">
        <p :class="$style.label">メッセージの本文</p>
        <DolphinTextarea v-model="model.body" :rows="8" :class="$style.body" />
        <EnqueteFormError
          v-if="formErrorManager.get('answeredChatMessage.body')"
          >{{
            formErrorManager.get('answeredChatMessage.body')
          }}</EnqueteFormError
        >
      </div>
      <div :class="$style.formRow">
        <MessageAttachmentActions
          v-model="selections"
          :isDeleteAllSelectionsAllowed="true"
        />
        <EnqueteFormError v-if="selectionError">
          {{ selectionError }}</EnqueteFormError
        >
      </div>
    </div>
  </section>
</template>

<style module lang="scss">
.isSend {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.message {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.formRow {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  color: $color-text-and-object-high-emphasis;
  font-size: 14px;
  font-weight: bold;
}

.body {
  font-size: 14px;
}
</style>
